import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import { WebsitesData } from '../../Models/Models';
import { ChangesContext, SiteContext, UserContext } from '../../Contexts';
import { EntityColors } from '../../Themes/EntityColors';
import CustomTooltip from '../Tooltips/CustomTooltip';
import { Utils } from '../../Services/Utils';
import { WebsiteService } from '../../Services/WebsiteService';
import { FrontendEntityChangeService } from '../../Services/FrontendEntityChangeService';
import TableShell from '../Table/TableShell';
import WebsiteRebuildModal from '../Modals/WebsiteRebuildModal';

const WebsiteTableComponent = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Weby');
    }, []);

    const { getWebsites } = WebsiteService();
    const { rebuild } = FrontendEntityChangeService();
    const [websites, setWebsites] = useState<WebsitesData[] | null>(null);
    const [websiteToRebuild, setWebsiteToRebuild] = useState<WebsitesData | null>(null);
    const [isRebuilding, setIsRebuilding] = useState(false);
    const navigate = useNavigate();
    const { currentSite }: any = useContext(SiteContext);
    const { currentUser }: any = useContext(UserContext);
    const { setHasChanges } = useContext(ChangesContext);
    const { action, icon } = EntityColors.website;

    useEffect(() => {
        if (!currentUser.isSuperAdmin) {
            navigate('/404');
            return;
        }
        getWebsites().then(resp => setWebsites(resp.data)).catch(() => setWebsites([]));
    }, []);

    const confirmRebuild = async () => {
        if (!websiteToRebuild) return;

        setIsRebuilding(true);
        const success = await rebuild(websiteToRebuild.id);
        setIsRebuilding(false);

        // A rebuild publishes every pending change, so the current site's sync indicator is stale.
        if (success && websiteToRebuild.id === currentSite.id) setHasChanges(false);
        setWebsiteToRebuild(null);
    };

    return (
        <>
            <TableShell
                title="Weby"
                icon={icon}
                accentColor={action}
                isLoading={websites === null}
                isEmpty={websites !== null && websites.length === 0}
                emptyMessage="Žiadne weby"
            >
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Skratka</th>
                            <th>Doména</th>
                            <th className="w-1 text-center">Akcia</th>
                        </tr>
                    </thead>
                    <tbody>
                        {websites?.map(website => (
                            <tr key={website.id}>
                                <td className="st-cell-strong">{website.shortName}</td>
                                <td className="st-cell-muted">{website.domainName}</td>
                                <td className="text-center">
                                    <CustomTooltip placement="right" message="Prebudovať" tooltipId={`website-rebuild-${website.id}`}>
                                        <button
                                            type="button"
                                            className="st-btn st-btn--fill st-btn--icon"
                                            style={{ '--btn-accent': action } as React.CSSProperties}
                                            disabled={isRebuilding}
                                            onClick={() => setWebsiteToRebuild(website)}
                                        >
                                            <FontAwesomeIcon icon={faRotate} />
                                        </button>
                                    </CustomTooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableShell>

            <WebsiteRebuildModal
                show={websiteToRebuild !== null}
                onHide={() => setWebsiteToRebuild(null)}
                websiteName={websiteToRebuild?.shortName}
                isConfirming={isRebuilding}
                onConfirm={confirmRebuild}
            />
        </>
    );
};

export default WebsiteTableComponent;
